/**
 * Puanlama mantığı — saf. DOM yok, global yok, yan etki yok.
 *
 * Eski js/solutions.js'te bu hesap doğrudan CU.exp'i okuyup DOM'a yazan bir
 * fonksiyonun içindeydi; test edilemezdi. Girdiyi parametre yapmak modül
 * sınırının ilk somut kazancı: artık test edilebilir.
 */

export interface Entry {
  date: string;
  type: 'income' | 'expense';
  amount: number;
  cat: string;
  cur: string;
  note?: string;
  per?: string;
}

export type ScoreKey = 'prot' | 'inv' | 'fit';
export type Scores = Record<ScoreKey, number | null>;
export type Strength = 'none' | 'weak' | 'moderate' | 'strong';

const clamp = (n: number) => Math.max(5, Math.min(100, n));
const uniq = <T,>(xs: T[]) => new Set(xs).size;

export function calcScores(entries: Entry[], now: Date = new Date()): Scores {
  const y = now.getFullYear();
  const m = now.getMonth();
  const thisMonth = entries.filter((e) => {
    const d = new Date(e.date);
    return d.getFullYear() === y && d.getMonth() === m;
  });
  const expE = thisMonth.filter((e) => e.type === 'expense');
  const incE = thisMonth.filter((e) => e.type === 'income');
  const totalExp = expE.reduce((a, e) => a + e.amount, 0);
  const totalInc = incE.reduce((a, e) => a + e.amount, 0);

  // Mali disiplin: kayıt tutma alışkanlığının zenginliği
  let prot: number | null = null;
  if (entries.length > 0) {
    const noteRatio = entries.filter((e) => e.note?.trim()).length / entries.length;
    const perRatio = entries.filter((e) => e.per?.trim()).length / entries.length;
    const dayScore = Math.min(uniq(entries.map((e) => e.date.substring(0, 7))) / 6, 1);
    const catScore = Math.min(uniq(entries.map((e) => e.cat)) / 5, 1);
    prot = clamp(Math.round(noteRatio * 25 + perRatio * 25 + dayScore * 30 + catScore * 20));
  }

  // Büyüme: bu ayki gelir-gider dengesi ve gider çeşitliliği
  let inv: number | null = null;
  if (thisMonth.length > 0) {
    const ratio = totalInc > 0 ? (totalInc - totalExp) / totalInc : 0;
    const balance = Math.max(0, Math.min(1, (ratio + 0.2) / 1.2));
    const diversity = Math.min(uniq(expE.map((e) => e.cat)) / 4, 1);
    inv = clamp(Math.round(balance * 60 + (incE.length > 0 ? 20 : 0) + diversity * 20));
  }

  // Olgunluk: tutarlılık, iki yönlü kayıt, güncellik, kategori genişliği
  let fit: number | null = null;
  if (entries.length >= 3) {
    const consistency = Math.min(entries.length / 20, 1);
    const bothTypes = expE.length > 0 && incE.length > 0 ? 1 : 0.4;
    const lastDate = entries.reduce((a, e) => (e.date > a ? e.date : a), '');
    const daysSince = (now.getTime() - new Date(lastDate).getTime()) / 86_400_000;
    const recency = daysSince < 7 ? 1 : daysSince < 30 ? 0.6 : 0.3;
    const catBreadth = Math.min(uniq(entries.map((e) => e.cat)) / 4, 1);
    fit = clamp(Math.round(consistency * 35 + bothTypes * 35 + recency * 20 + catBreadth * 10));
  }

  return { prot, inv, fit };
}

export function strengthOf(score: number | null): Strength {
  if (score === null) return 'none';
  if (score >= 80) return 'strong';
  if (score >= 40) return 'moderate';
  return 'weak';
}

/** En çok harcanan / en çok kazandıran ilk 5 kalem. */
export function topFive(
  entries: Entry[],
  type: 'expense' | 'income',
): Array<{ label: string; cur: string; total: number }> {
  const rows = entries.filter((e) => e.type === type);
  const acc = new Map<string, { label: string; cur: string; total: number }>();
  for (const e of rows) {
    const label = type === 'expense' ? e.cat : e.note || e.cat;
    const key = `${label}||${e.cur}`;
    const hit = acc.get(key) ?? { label, cur: e.cur, total: 0 };
    hit.total += e.amount;
    acc.set(key, hit);
  }
  return [...acc.values()].sort((a, b) => b.total - a.total).slice(0, 5);
}
